import type { Palette, Theme } from '@mui/material'
import type { DefaultTheme } from '@mui/styles'

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    primary: true
    secondary: true
    secondaryOutlined: true
    green: true
    red: true
    default: true
    outlinedOrange: true
  }
}

interface IPalette extends Partial<Palette> {
  primary: Palette['primary'] & {
    hover: string
    font: string
    darkGray: string
    high: string
    icon: string
  }
}

declare module '@mui/styles' {
  export interface DefaultTheme extends Theme {
    name?: string
    palette: IPalette
    shadow: {
      subtle: string
    }
  }
}

export default {
  spacing: 8,
  breakpoints: {
    values: {
      xs: 320,
      sm: 768,
      md: 1025,
      lg: 1200,
      xl: 1536,
    },
  },
  shadow: {
    subtle: 'rgb(0 0 0 / 17%) 0px 0px 8px 1px',
  },
  // Palette values must be static color formats (hex/rgb/rgba/hsl) — MUI internally
  // calls darken()/lighten() on palette colors at render time to compute hover and
  // active states, and those functions can't parse CSS variables. Dark mode is
  // applied via the CSS variables in _theme.scss for rules that consume them
  // directly; MUI's default palette stays at the light-theme values.
  palette: {
    primary: {
      main: '#ffffff',
      secondary: '#f5f8fd',
      dark: '#2c2c2c',
      light: '#DBE1FF',
      contrastText: '#ffffff',
      blue: '#2F80ED',
      darkGray: '#2c2c2c',
      lightGray: '#c0c0c0',
      blueOrchid: '#2F80ED',
      hover: '#2482d8',
      font: '#e8eae6',
      icon: '#536976',
      high: '#D0E4F7',
    },
    warning: {
      main: '#009688',
    },
    secondary: {
      main: '#90F8B2',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    success: {
      main: '#2dca73',
      light: '#2dca73',
      dark: '#1e9a5a',
      contrastText: '#ffffff',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          height: '48px',
          minWidth: 'unset',
          borderRadius: 8,
          width: '100%',
          textTransform: 'initial',
          fontWeight: 700,
          fontSize: '14px',

          '&.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.26)',
            background: '#c7c7c747',
            border: 'none',
          },
        },
      },
      variants: [
        {
          props: {
            variant: 'primary',
          },
          style: {
            background: '#FFF',
            color: '#2C2C2C',
            border: '1px solid #FFF',
            '&:hover': {
              background: '#C0C0C0',
            },
          },
        },
        {
          props: {
            variant: 'secondary',
          },
          style: {
            background: '#495B71',
            color: 'white',
            border: '1px solid #495B71',
          },
        },
        {
          props: {
            variant: 'secondaryOutlined',
          },
          style: {
            background: 'transparent',
            color: '#20374F',
            border: '1px solid #828282',
          },
        },
        {
          props: {
            variant: 'green',
          },
          style: {
            background: '#20B59A',
            color: 'white',
            border: '1px solid #20B59A',

            '&:hover': {
              backgroundColor: '#20b59a26',
              color: '#20B59A',
            },
          },
        },
        {
          props: {
            variant: 'red',
          },
          style: {
            background: '#EA4235',
            color: 'white',
            border: '1px solid #EA4235',

            '&:hover': {
              backgroundColor: '#ea42352e',
              color: '#EA4235',
            },
          },
        },
        {
          props: {
            variant: 'outlinedOrange',
          },
          style: {
            background: 'transparent',
            color: '#F36B32',
            border: '1px solid #F36B32',

            '&.Mui-disabled': {
              background: '#ffa50000',
              border: '1px solid #d7d7d761',
              color: '#ffffff5c',
            },

            '&:hover': {
              backgroundColor: '#ffffff14',
              color: 'white',
              border: '1px solid #ffffff14',
            },
          },
        },
        {
          props: {
            variant: 'outlined',
          },
          style: {
            background: 'transparent',
            color: 'white',
            border: '1px solid #FFF',

            '&.Mui-disabled': {
              background: '#ffa50000',
              border: '1px solid #d7d7d761',
              color: '#ffffff5c',
            },

            '&:hover': {
              backgroundColor: '#ffffff14',
              color: 'white',
              border: '1px solid #ffffff14',
            },
          },
        },
        {
          props: {
            variant: 'default',
          },
          style: {
            background: 'transparent',
            color: '#F36B32',
            border: '1px solid #F36B32',
          },
        },
      ],
    },
    MuiInput: {
      styleOverrides: {
        root: {
          width: '100%',
          height: '48px',
          background: '#ffffff',
          border: '1px solid #f2f2f2',
          boxSizing: 'border-box',
          borderRadius: '8px',
          fontSize: '14px',
          color: '#636f7e',
          padding: '0 14px',
          textTransform: 'initial',

          '&.Mui-disabled': {
            background: '#afacac3b',
            border: 'none',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: 'white',
        },
      },
    },
  },
} as unknown as DefaultTheme
